import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Group, Input, InputAddon, Stack, Text } from '@chakra-ui/react';
import { LinkSimpleHorizontal, X } from '@phosphor-icons/react';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { fetchAPI } from '@/api/fetch';
import { apiUpdateMeetingURL } from '@/api/services/meeting.service';
import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Meeting, MeetingSummary } from '@/types/Meeting';

interface FormValues {
  meeting_url: string;
}

const ZOOM_URL_REGEX = /^https:\/\/.*\.zoom\.us\/.*$/;

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700">
      {message}
    </Box>
  ) : null;

export default function UpdateMeetingLinkDialog({
  meeting,
  onEditMeeting,
  isOpen = false,
  onClose,
  disableClose = false
}: {
  meeting: MeetingSummary;
  onEditMeeting: (meeting: MeetingSummary) => void;
  isOpen?: boolean;
  onClose?: () => void;
  disableClose?: boolean;
}) {
  const [submitError, setSubmitError] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>();

  const handleFormSubmit = async (formData: FormValues) => {
    const { meeting_url } = formData;

    try {
      const response = await fetchAPI<Meeting>({
        request: () => apiUpdateMeetingURL(meeting.meeting_id, { meeting_url })
      });

      if (response.success) {
        const updatedMeeting = response.data;
        onEditMeeting({
          title: updatedMeeting.title,
          meeting_start_time: updatedMeeting.meeting_start_time,
          meeting_end_time: updatedMeeting.meeting_end_time,
          meeting_url: updatedMeeting.meeting_url,
          meeting_id: updatedMeeting.meeting_id,
          timestamp: updatedMeeting.timestamp,
          status: updatedMeeting.status
        });
        onClose?.();
      } else {
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
      setSubmitError(true);
    }
  };

  const handleDialogClose = () => {
    reset();
    setSubmitError(false);
    onClose?.();
  };

  return (
    <DialogRoot
      size="xl"
      open={isOpen}
      onOpenChange={({ open }) => {
        if (open) {
          reset();
        }
        if (!open) {
          onClose?.();
        }
      }}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      unmountOnExit
    >
      <DialogContent borderRadius="12px">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Invalid Meeting Link</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4">
              <Text fontSize="16px" color="neutrals.700">
                It looks like the meeting link is invalid or unsupported. Please double-check the
                link and try again.
              </Text>
              {/* Meeting Link Field */}
              <Field
                label={<RequiredFieldLabel>Meeting Link</RequiredFieldLabel>}
                invalid={!!errors.meeting_url}
                errorText={<ErrorMessage message={errors.meeting_url?.message} />}
              >
                <Text fontSize="14px" color="neutrals.500">
                  Capture meetings from Zoom.
                </Text>
                <Group attached w="100%">
                  <InputAddon fontSize="16px" bg="background.secondary" borderRadius="15px">
                    <LinkSimpleHorizontal size={24} aria-hidden="true" />
                  </InputAddon>
                  <Input
                    disabled={isSubmitting}
                    size="lg"
                    placeholder="http://www.zoom.us"
                    w="100%"
                    type="text"
                    borderRadius="15px"
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    aria-label="Meeting URL"
                    {...register('meeting_url', {
                      required: 'Meeting link is required',
                      pattern: {
                        value: ZOOM_URL_REGEX,
                        message: 'Please enter a valid Zoom link'
                      }
                    })}
                  />
                </Group>
              </Field>
            </Stack>

            {/* Error Message */}
            {submitError && (
              <Box color="error.700" fontSize="16px" mt="16px">
                Failed to update meeting. Please try again.
              </Box>
            )}
          </DialogBody>

          {/* Dialog Footer */}
          <DialogFooter>
            {!disableClose && (
              <DialogActionTrigger asChild>
                <Button
                  w="160px"
                  borderColor="primary.900"
                  background="background.secondary"
                  color="primary.900"
                  borderRadius="8px"
                  fontSize="16px"
                  disabled={isSubmitting}
                  aria-label="Cancel"
                >
                  Cancel
                </Button>
              </DialogActionTrigger>
            )}
            <Button
              type="submit"
              w="160px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              loading={isSubmitting}
              aria-label="Submit"
            >
              Confirm
            </Button>
          </DialogFooter>
        </form>
        {!disableClose && (
          <DialogCloseTrigger m="12px" disabled={isSubmitting}>
            <X size={32} weight="bold" aria-label="Close dialog" />
          </DialogCloseTrigger>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
